import React, { useCallback, useEffect, useRef, useState } from "react";
import LogoQR from "assets/icons/QR.png";
import classes from "./Login.module.scss";
import QRCode from "react-qr-code";
import CircularIndeterminate from "components/utils/spinner/Spinner";
import { LinearProgress } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { FormikSelectInput } from "components/Filters/FormikInputs";
// Services
import { loginQREvent, loginQRGet, loginQRMerchant, loginServicioGet } from "services/services-mia/login";
import { usuarioMinimosGet } from "services/services-mia";
import { merchantLogoGet } from "services/services-mia/merchant";

import { checkAuth } from "services/checkAuth";
// Recoil
import {
  userCredentials,
  snackbarData,
  userInfo,
  miaServices,
  userCoords,
} from "recoilState/GlobalState";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { LogoMia } from "assets/icons";
import { Routes } from "consts/Routes-App";
import { useLocation, useNavigate } from "react-router-dom";

export default function Login() {
  const setCredentials = useSetRecoilState(userCredentials);
  const setSnackbarInfo = useSetRecoilState(snackbarData);
  const setInfo = useSetRecoilState(userInfo);
  const setServices = useSetRecoilState(miaServices);
  const navigate = useNavigate();
  const location = useLocation();

  const coord = useRecoilValue(userCoords);

  const [UUID, setUUID] = useState();
  const [loading, setLoading] = useState(false);
  const [expirado, setExpirado] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressTimer, setProgressTimer] = useState();
  const intentos = useRef(0);
  const from = useRef(location.state?.from?.pathname + location.state?.from?.search || Routes.dashboard.route);

  const formik = useFormik({
    initialValues: {
      merchant: null,
    },
    validationSchema: yup.object({
      merchant: yup.string().required("Seleccione un cliente"),
    }),
    onSubmit: async (values) => {
      const merchant = merchants.find(
        (merchant) => merchant.merchantId === parseInt(values.merchant)
      );
      await loginDo(UUID, merchant);
    },
  });

  useEffect(() => {
    if (checkAuth()) {
      navigate(from.current, { replace: true });
      return;
    }

    return () => {
      clearInterval(progressTimer);
    };
  }, []);

  useEffect(() => {
    if (coord?.lat && coord?.lng) getQR();
  }, [coord]);

  useEffect(() => {
    if (UUID) {
      initProgressTimer();
      getQREvent(UUID);
    }
  }, [UUID]);

  const getQR = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!coord?.lat || !coord?.lng) return;
    setExpirado(false);
    setLoading(true);
    const { result } = await loginQRGet({
      sesionLatitud: coord.lat.toFixed(6),
      sesionLongitud: coord.lng.toFixed(6),
    });
    const data = result[0]

    setUUID(data.codigoQR);
    setLoading(false);
    return data.codigoQR;
  }, [coord]);

  const initProgressTimer = () => {
    setProgress(0);
    const timer = setInterval(() => {
      if (progress >= 100) {
        clearInterval(timer);
      }
      setProgress((oldProgress) => (
        //140 segundos en 580 saltos hasta completar 100%
        (oldProgress >= 100)
          ? 100
          : oldProgress + (100 / 580)
      ));
    }, ((140 * 1000) / 580));
    setProgressTimer(timer);
  }

  const loginDo = useCallback(async (lbmUUID, merchant) => {
    const { result: credentials, status } = await loginQRMerchant({
      codigoQR: lbmUUID,
      merchantId: merchant.merchantId,
    });
    if (status.code !== 1) {
      setSnackbarInfo({
        open: true,
        message: "Login no autorizado",
        severity: "error",
      });
      setTimeout(() => {
        setSnackbarInfo({
          open: false,
          message: "",
          severity: "error",
        });
      }, 5000);
      return
    }
    localStorage.setItem("hooliToken", credentials[0].hooliToken);
    localStorage.setItem("sessionId", credentials[0].sessionId);
    localStorage.setItem("merchantId", merchant.merchantId);
    localStorage.setItem("merchantNombre", merchant.merchantNombre);
    localStorage.setItem("merchantRZ", merchant.merchantRZ);
    localStorage.setItem("merchantCUIT", merchant.merchantCUIT);
    const newCredentials = {
      ...credentials[0],
      ...merchant,
    };
    setCredentials(newCredentials);

    const [userData, getMerchantLogoResponse, getServicesResponse] = await Promise.all([
      usuarioMinimosGet(newCredentials),
      merchantLogoGet({}, newCredentials),
      loginServicioGet({}, newCredentials)
    ])
    setInfo({
      ...getMerchantLogoResponse.result[0],
      ...userData
    });
    setServices(getServicesResponse.result);

    navigate(from.current, { replace: true });
  }, [navigate, setCredentials, setInfo, setServices, setSnackbarInfo])

  const getQREvent = useCallback(async (lbmUUID) => {
    intentos.current++;
    const { result: merchants, status } = await loginQREvent({
      codigoQR: lbmUUID,
    });
    if (status.code !== 1) {
      if (status.action === 'X') {
        setUUID(null);
        switch (status.errcode) {
          case 30763:
            setExpirado(true);

            setSnackbarInfo({
              open: true,
              message: "Usuario no autorizado",
              severity: "error",
            });
            setTimeout(() => {
              setSnackbarInfo({
                open: false,
                message: "",
                severity: "error",
              });
            }, 5000);
            return;
          default:
            console.warn('loginQREvent:', status)
            break;
        }
      }
      if (intentos.current > 2) {
        setExpirado(true);
        return;
      }
      getQR();
      return;
    }
    if (merchants.length === 0) {
      setSnackbarInfo({
        open: true,
        message: "Usuario no autorizado",
        severity: "error",
      });
      setTimeout(() => {
        setSnackbarInfo({
          open: false,
          message: "",
          severity: "error",
        });
      }, 5000);
      return;
    }
    if (merchants.length === 1) {
      await loginDo(lbmUUID, merchants[0])
      return;
    }
    setMerchants(merchants);
    formik.setFieldValue("merchant", merchants[0].merchantId);
  }, [formik, getQR, loginDo, setSnackbarInfo]);

  return (
    <main className={classes.root}>
      <div className={classes.logoContainer}>
        <img src={LogoMia} alt="logo-mia" />
      </div>
      <div className={classes.login}>
        <div className={classes.container}>
          <div className={classes.qrContainer}>
            {expirado ? (
              <>
                <h4>Ups!</h4>
                <p>
                  El código caducó, generá un{" "}
                  <span>nuevo QR</span> para poder ingresar.
                </p>
                <img src={LogoQR} alt="Celular" />
                <button
                  className={classes.button}
                  onClick={getQR}
                >
                  Generar QR
                </button>
              </>
            ) : (
              <>
                {!loading ? (
                  merchants.length > 1 ? (
                    <form
                      onSubmit={formik.handleSubmit}
                      className={classes.form}
                    >
                      <FormikSelectInput
                        formik={formik}
                        data={{
                          merchant: {
                            label: "Seleccionar comercio",
                          },
                        }}
                        name="merchant"
                        options={merchants}
                        optionKey="merchantId"
                        optionValue="merchantId"
                        optionName="merchantRZ"
                      />
                      <button
                        type="submit"
                        disabled={
                          !formik.values.merchant
                        }
                        className={classes.button}
                      >
                        Ingresar
                      </button>
                    </form>
                  ) : (
                    <>
                      <h4>Hola!</h4>
                      <p>
                        Para poder ingresar, escanea el{" "}
                        <span>QR</span> desde la App de
                        MIA.
                      </p>
                    </>
                  )
                ) : null}
                {UUID && merchants.length === 0 && !loading && (
                  <>
                    <QRCode value={UUID} />
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      style={{
                        width: "100%",
                        marginTop: "16px",
                      }}
                      color="secondary"
                    />
                  </>
                )}
                {loading && <CircularIndeterminate />}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
